/**
 * useExitIntent.ts — "the patient looks like they are about to close the tab".
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey copy pass
 * (the CTO, 2026-09-26).
 *
 * WHY. The questionnaire's "Leave this step?" retention dialog used to open on
 * Back, which is the one moment it gets in the way: a patient going back to fix
 * an answer met a pop-up instead of the previous question (Ashley
 * Haveloch-Jones: "it puts me on this pop up rather than being able to go back
 * to the previous step uninterrupted"). The CTO: "only show this if they are
 * trying to close the tab or something". A browser never lets a page draw its
 * own dialog on the close itself (that is the browser's generic "Leave site?"
 * prompt, which the unsaved-changes guard still raises). The nearest honest
 * signal is the pointer leaving the page through its TOP edge — towards the tab
 * strip, the close button or the address bar.
 *
 * RULES.
 *   - DESKTOP POINTERS ONLY: `(hover: hover) and (pointer: fine)`. A phone has no
 *     pointer to leave with, and a touch "mouseout" is not an intent.
 *   - ONCE per mount: a dialog that comes back every time the pointer brushes
 *     the tab strip is a nag, and a nag is how people learn to ignore it.
 *   - NEVER OVER ANOTHER DIALOG: if a dialog is already open (a call back, "Why
 *     we ask") the signal is ignored and NOT spent, so it can still fire later.
 *   - No `matchMedia` (a test runner, a server render) ⇒ never fires.
 */
import { useEffect, useRef } from 'react';

const DESKTOP_POINTER = '(hover: hover) and (pointer: fine)';

function desktopPointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DESKTOP_POINTER).matches;
}

export function useExitIntent(enabled: boolean, onIntent: () => void): void {
  const spent = useRef(false);
  const handler = useRef(onIntent);
  handler.current = onIntent;

  useEffect(() => {
    if (!enabled || spent.current || !desktopPointer()) return;
    function onMouseOut(event: MouseEvent) {
      // Leaving the DOCUMENT (no element to go to) through its top edge.
      if (event.relatedTarget !== null || event.clientY > 0 || spent.current) return;
      if (document.querySelector('[role="dialog"]')) return;
      spent.current = true;
      document.removeEventListener('mouseout', onMouseOut);
      handler.current();
    }
    document.addEventListener('mouseout', onMouseOut);
    return () => document.removeEventListener('mouseout', onMouseOut);
  }, [enabled]);
}
